import { AnyAction } from 'redux';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefnProps } from 'configuration-schemas/confdefnComponentProps';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { formatViewDefnError } from 'configuration-schemas/viewdefns/general';
import service from 'src/ServiceContainer';
import { ViewDefnState } from 'src/dao/tenantConfigClient';
import { AppThunkDispatch } from 'src/store';
import { ViewDataState } from 'src/types/Domain';
import { ComponentErrorType, ErrorBoundaryComponentError } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { CollectionAnalysisViewDefn, zCollectionAnalysisViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { cacheCheckFetchPivotData } from 'src/pages/Hindsighting/StyleColorReview/StyleColorReview.slice';
import { ListDataOptions } from 'src/worker/pivotWorker.types';

export interface CollectionAnalysisSlice {
  viewDefn?: CollectionAnalysisViewDefn;
  viewDefnState: ViewDefnState;
  collectionDataState: ViewDataState;
  collectionAnalysisCacheHash: string | null;
  itemsDataState: ViewDataState;
  collectionAnalysisItemsCacheHash: string | null;
  anchorDataState: ViewDataState;
  anchorCacheHash: string | null;
  clusterDataState: ViewDataState;
  clusterCacheHash: string | null;
}

const initialState: CollectionAnalysisSlice = {
  viewDefnState: ViewDefnState.idle,
  viewDefn: undefined,
  collectionDataState: ViewDataState.idle,
  itemsDataState: ViewDataState.idle,
  anchorDataState: ViewDataState.idle,
  clusterDataState: ViewDataState.idle,
  collectionAnalysisCacheHash: null,
  collectionAnalysisItemsCacheHash: null,
  anchorCacheHash: null,
  clusterCacheHash: null,
};

const collectionAnalysisSlice = createSlice({
  name: 'CollectionAnalysis',
  initialState,
  reducers: {
    requestCollectionAnalysisConfig(state) {
      state.viewDefnState = ViewDefnState.loading;
    },
    receiveCollectionAnalysisConfig(state, action: PayloadAction<CollectionAnalysisViewDefn>) {
      state.viewDefn = action.payload;
      state.viewDefnState = ViewDefnState.loaded;
    },
    requestCollectionData(state) {
      state.collectionDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveCollectionCacheHash(state, action: PayloadAction<string>) {
      state.collectionAnalysisCacheHash = action.payload;
    },
    receiveCollectionCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.collectionAnalysisCacheHash) {
        state.collectionDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveCollectionLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.collectionAnalysisCacheHash) {
        state.collectionDataState = ViewDataState.liveDataReady;
      }
    },
    requestItemsData(state) {
      state.itemsDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveItemsCacheHash(state, action: PayloadAction<string>) {
      state.collectionAnalysisItemsCacheHash = action.payload;
    },
    receiveItemsCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.collectionAnalysisItemsCacheHash) {
        state.itemsDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveItemsLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.collectionAnalysisItemsCacheHash) {
        state.itemsDataState = ViewDataState.liveDataReady;
      }
    },
    requestAnchorData(state) {
      state.anchorDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveAnchorCacheHash(state, action: PayloadAction<string>) {
      state.anchorCacheHash = action.payload;
    },
    receiveAnchorCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.anchorCacheHash) {
        state.anchorDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveAnchorLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.anchorCacheHash) {
        state.anchorDataState = ViewDataState.liveDataReady;
      }
    },
    requestClusterData(state) {
      state.clusterDataState = ViewDataState.liveDataLoadingNoCache;
    },
    receiveClusterCacheHash(state, action: PayloadAction<string>) {
      state.clusterCacheHash = action.payload;
    },
    receiveClusterCachedData(state, action: PayloadAction<string>) {
      // Ignore receipts from loads unrelated to current fetch.
      // (This could entirely be replaced with an epic for all screens using this technique.)
      if (action.payload === state.anchorCacheHash) {
        state.clusterDataState = ViewDataState.liveDataLoadingFoundCache;
      }
    },
    receiveClusterLiveData(state, action: PayloadAction<string>) {
      if (action.payload === state.anchorCacheHash) {
        state.clusterDataState = ViewDataState.liveDataReady;
      }
    },
    receiveError(_state, _action: PayloadAction<ErrorBoundaryComponentError>) {
      return initialState;
    },
    cleanUp() {
      return initialState;
    },
  },
});

export const {
  requestCollectionAnalysisConfig,
  receiveCollectionAnalysisConfig,
  requestCollectionData,
  receiveCollectionCacheHash,
  receiveCollectionCachedData,
  receiveCollectionLiveData,
  requestItemsData,
  receiveItemsCacheHash,
  receiveItemsCachedData,
  receiveItemsLiveData,
  requestAnchorData,
  receiveAnchorCacheHash,
  receiveAnchorCachedData,
  receiveAnchorLiveData,
  requestClusterData,
  receiveClusterCacheHash,
  receiveClusterCachedData,
  receiveClusterLiveData,
  cleanUp,
  receiveError,
} = collectionAnalysisSlice.actions;

export default collectionAnalysisSlice.reducer;

export function fetchCollectionAnalysisConfigs(defns: DefnProps) {
  return async (dispatch: AppThunkDispatch): Promise<AnyAction | void> => {
    try {
      const configResponse = await service.tenantConfigClient.getTenantViewDefns<CollectionAnalysisViewDefn>({
        defnIds: defns.view,
        validationSchemas: [zCollectionAnalysisViewDefn],
      });

      dispatch(receiveCollectionAnalysisConfig(configResponse[0]));
    } catch (error) {
      const err = error as any;
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: formatViewDefnError(error),
          name: ConfDefnComponentType.collectionAnalysis,
          issues: error,
          defnId: err.defnId,
        }),
      );
    }
  };
}

export function fetchCollectionAnalysis(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestCollectionData,
    receiveCollectionCacheHash,
    receiveCollectionCachedData,
    receiveCollectionLiveData,
  );
}

export function fetchCollectionAnalysisItems(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestItemsData,
    receiveItemsCacheHash,
    receiveItemsCachedData,
    receiveItemsLiveData,
  );
}

export function fetchCollectionAnalysisAnchorData(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestAnchorData,
    receiveAnchorCacheHash,
    receiveAnchorCachedData,
    receiveAnchorLiveData,
  );
}

export function fetchCollectionAnalysisClusterData(model: string, options: ListDataOptions) {
  return cacheCheckFetchPivotData(
    service.pivotService.listDataCacheCheck(model, options),
    requestClusterData,
    receiveClusterCacheHash,
    receiveClusterCachedData,
    receiveClusterLiveData,
  );
}
